import React from 'react'
import { useOne, useList } from '@refinedev/core'
import { Button, Card, Skeleton, Space, Typography } from 'antd'
import { Link } from 'react-router'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { headStyle, Field, fmtDate } from './reportUiKit'
import { memoryCommentPath } from '../memory_comments/list'
import type { Report } from './show'

type MemoryComment = Tables<'memory_comments'>
type Profile = Tables<'profiles'>

/**
 * Reported-content card for a `memory_comment` report — the same "Reported
 * content" card shell as ReportedContent, but for a memory comment
 * (public.memory_comments), which kindSpec() in businesses/contentModeration
 * has no entry for (that catalog is Jaguar-Owned content only). Rendered by
 * ReportedContent's delegation, one level up.
 *
 * NO delete or remove action here. Deletion stays on the Memory Comments
 * page (memory_comments/list.tsx), which already has its own confirm — one
 * removal surface, not two. Also: memory comments have no `status` column
 * (they are never "removed" in place, only deleted outright), so the
 * Jaguar-Owned remove/restore pattern in ReportedContent does not apply here.
 */
export function ReportedMemoryComment({ report }: { report: Report }) {
  // retry: false — the error copy below already tells the admin to refresh
  // manually, so there is no reason to sit on the loading skeleton through
  // several silent automatic retries before it ever shows.
  const { query, result } = useOne<MemoryComment>({
    resource: 'memory_comments',
    id: report.target_id,
    queryOptions: { enabled: !!report.target_id, retry: false },
  })
  const comment = result && result.id ? result : null

  // Authors key on profiles.user_id, never profiles.id: the two differ in
  // every row, and an id match would name nobody.
  const { result: profilesResult, query: profilesQuery } = useList<Profile>({
    resource: 'profiles',
    filters: [{ field: 'user_id', operator: 'eq', value: comment?.user_id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: !!comment?.user_id },
  })
  const name = profilesResult?.data?.[0]?.name

  if (!comment) {
    // isPending, not isLoading: a first fetch paused offline (or a query
    // that hasn't started because target_id is empty) reports isLoading:
    // false while status stays "pending" (TanStack Query 5's isPending trap,
    // CLAUDE.md). Keying this off isLoading would fall through to the
    // not-found copy below for a comment that hasn't even been looked up
    // yet — exactly the false claim this component's own header comment
    // warns against.
    if (query.isPending) {
      return (
        <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
          <Skeleton active />
        </Card>
      )
    }
    // Distinguish a real not-found (getOne resolves with no row, no error)
    // from a failed fetch (network error, 5xx, or a target_id that isn't a
    // uuid and 400s PostgREST) — the two look identical once isPending is
    // false unless isError is checked first. Reporting "no longer exists" on
    // an error would be false, and could lead an admin to dismiss a report
    // on a comment that's still there.
    if (query.isError) {
      return (
        <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
          <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, fontStyle: 'italic' }}>
            Couldn't load this comment. Refresh to try again.
          </Typography.Text>
        </Card>
      )
    }
    // Reached only on query.isSuccess (isPending and isError are the only
    // other statuses, both handled above) — a real not-found: getOne
    // resolved with no row and no error.
    return (
      <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
        <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, fontStyle: 'italic' }}>
          This comment no longer exists. Its author or an admin may have deleted it.
        </Typography.Text>
      </Card>
    )
  }

  return (
    <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
      <Field label="Author">{name ?? (profilesQuery.isLoading ? '…' : 'Unknown member')}</Field>
      <Field label="Posted">{fmtDate(comment.created_at)}</Field>
      <Field label="Comment">
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: brand.fSans }}>
          {comment.body}
        </Typography.Paragraph>
      </Field>

      <Space wrap>
        <Link to={memoryCommentPath(comment.id)} data-testid="reported-memory-comment-open">
          <Button>Open in Memory Comments</Button>
        </Link>
        <Link to={`/photos/show/${comment.photo_id}`} data-testid="reported-memory-comment-photo">
          <Button>Open the memory</Button>
        </Link>
      </Space>
    </Card>
  )
}
